'use client'

import { useMemo, useState } from 'react'
import { useLowStock } from '@/components/menu/LowStockContext'
import { withLowStockTag } from '@/components/menu/LowStockBadge'
import Image from 'next/image'
import { motion } from 'framer-motion'
import { X } from 'lucide-react'
import { useTranslations, useLocale } from 'next-intl'
import { localizedItem, type ItemTranslations } from '@/lib/menu/localizeItem'
import { modifierGroupLabel } from '@/lib/menu/modifierGroupLabel'
import { localizedName, localizedGroupLabel, type NameTranslations } from '@/lib/menu/localizedName'
import type { Locale } from '@/lib/i18n/config'
import { SheetPortal } from './SheetPortal'
import { ZonedBuilder, type ZonedGroup } from '@/components/menu/ZonedBuilder'
import { zonedCartRows } from '@/lib/menu/zonedCartRows'
import type { PricedZonedSelection } from '@/lib/menu/zonedBuilder'
import type { ZonedClientFacts } from '@/lib/menu/zonedClientFacts'
import { menuPhotoUrl } from '@/lib/utils/imageTransform'
import { focalPosition } from '@/lib/menu/photoFocal'
import { resolveGroupOptionPhoto } from '@/lib/menu/resolveOptionPhoto'
import { groupShowsPhotos, orderOptionsByPhoto } from '@/lib/menu/optionPhotoLayout'
import type { CartItem } from './types'
import { effectiveItemCardPrice, effectiveUnitPrice, type PricingConfig } from '@/lib/utils/pricing'
import {
  MAX_MODIFIER_QTY,
  canRepeat,
  groupMaxUnits,
  groupSelectionValid,
  modifierUnitsSubtotal,
  normalizeGroupRule,
  type ModGroupRule,
  type ModOptionRule,
} from '@/lib/menu/modifierQuantity'
import { applyRulesToLineModifiers, groupRule, ruleNoteOf } from '@/lib/menu/includedSelectionsCart'
import { RetailLinePicker } from './RetailLinePicker'
import { retailStockBadge, retailVariantStock, useRetailStock } from './useRetailStock'
import { micrositeLineSubtotal } from '@/lib/microsite/couponUx'
import {
  buildRetailCartLine,
  initialWeightMilli,
  isRetailVariantItem,
  isRetailWeightItem,
  needsRetailPicker,
  onlineWeightUnitOf,
  selectableVariants,
  type RetailPickerFacts,
} from '@/lib/microsite/retailLine'

type ModifierOption = {
  id: string
  name: string
  /** Traducciones por locale del rótulo (Menú → Idiomas). El canónico va a cocina. */
  translations?: NameTranslations | null
  price_delta: number
  is_available: boolean | null
  is_default?: boolean | null
  /** «Va sola»: ocupa todo el cupo incluido del grupo (lib/menu/includedSelections). */
  uses_all_included?: boolean | null
  /** Foto propia del dueño; null = la del catálogo (lib/menu/resolveOptionPhoto). */
  image_url?: string | null
  /** El dueño la dejó sin foto, aunque el catálogo tenga una. */
  hide_photo?: boolean | null
}

type ModifierGroup = {
  id: string
  /** Nombre interno del grupo. NO se le muestra al cliente: usa modifierGroupLabel(). */
  name: string
  /** Rótulo que el dueño escribió para el cliente ("Elige tu proteína"). */
  display_name?: string | null
  /** Traducciones por locale: `name` traduce el interno y `description` el display_name. */
  translations?: NameTranslations | null
  required: boolean | null
  min_selections: number | null
  max_selections: number | null
  /** D8: false para masa y tamaño — la cocina no puede partirlos. */
  zonable?: boolean | null
  /** «Mostrar con fotos» del grupo; llega en false también si el local apagó las
   *  fotos de modificadores (lib/menu/modifierPhotosSetting). */
  show_photos?: boolean | null
  /** Merchant marking: 'normal' (default) or 'removal'. Consumed by classifyOption engine. */
  display_style?: string | null
  /** Selecciones incluidas (el mixto). Ausentes = sin regla. */
  included_selections?: number | null
  extra_selection_price?: number | null
  included_shared_portion?: boolean | null
  modifier_options: ModifierOption[]
}

type Item = {
  id: string
  name: string
  description: string | null
  translations?: ItemTranslations | null
  price: number
  image_url: string | null
  image_focal?: string | null
  category_id?: string | null
  item_modifier_groups?: Array<{
    sort_order?: number | null
    modifier_groups: ModifierGroup | null
  }>
  /** Pizza armable por zonas. Ausente/false ⇒ el modal clásico, sin cambio alguno. */
  zoned_build?: boolean | null
  item_default_options?: Array<{ modifier_option_id: string }> | null
  zone_size_group_id?: string | null
} & RetailPickerFacts

type Props = {
  item: Item
  pricingCfg: PricingConfig
  accentColor: string
  /** Rejilla precio×tamaño y modo de precio por zona. Se pide EXPLÍCITO —y no por
   *  contexto— para que TypeScript obligue a todo lugar nuevo que abra este modal a
   *  pasarlo: olvidarlo devolvería el precio plano sin que nadie se entere. */
  zonedFacts: ZonedClientFacts
  onAdd: (cartItem: CartItem) => void
  onClose: () => void
  /** Local de la tienda: con él, un producto por peso o con variantes pide su stock
   *  EN VIVO (Tarea 2b.3). Un plato no lo usa: el modal es el de main. */
  locationId?: string
}

// Mismo tope que POS/kiosk (cabe en ticket de cocina); el API acepta hasta 500.
const MAX_NOTE_LEN = 280

type SelectedOption = {
  modifier_group_id: string
  modifier_option_id: string
  option_name: string
  /** Precio efectivo (ya pasado por effectiveUnitPrice) POR UNIDAD. */
  price_delta: number
  /** Unidades del mismo modificador (≥ 1). Ver lib/menu/modifierQuantity.ts */
  quantity: number
}

/**
 * Grupos de modificadores renderizables de un ítem: descarta los que se
 * quedaron sin NINGUNA opción disponible, y pone los obligatorios primero.
 *
 * OJO — esto NO esconde ingredientes agotados sueltos: una opción en 86 se
 * dibuja apagada con "Agotado" dentro de su grupo (ver el render). Lo que se
 * descarta es el grupo ENTERO cuando no le queda ni una opción elegible, y el
 * filtro no es cosmético: sin él, un grupo obligatorio con todas sus opciones
 * agotadas exige un mínimo imposible y el botón "Agregar al carrito" queda
 * trabado sin salida. Mostrarlo entero apagado sería el mismo callejón.
 * Exportada para poder probarla — el componente no es testeable en este repo
 * (jest corre en testEnvironment "node", sin testing-library).
 */
export function renderableGroups(
  list: Array<{
    id: string
    required: boolean | null
    min_selections: number | null
    modifier_options: Array<{ is_available: boolean | null }>
  }>
) {
  const isRequired = (g: { required: boolean | null; min_selections: number | null }) =>
    g.required === true || (g.min_selections ?? 0) >= 1
  return [...list]
    .filter((g) => g.modifier_options.some((o) => o.is_available !== false))
    .sort((a, b) => Number(isRequired(b)) - Number(isRequired(a)))
}

export function ItemModal({ item, pricingCfg, accentColor, zonedFacts, onAdd, onClose, locationId }: Props) {
  const t = useTranslations('public.microsite')
  const lowStock = useLowStock()
  const tRules = useTranslations('modifierRules')
  // Texto de display según el idioma del cliente; el nombre canónico (item.name)
  // es el que va al carrito/orden — no se toca aquí (ver handleAdd).
  const locale = (useLocale() === 'en' ? 'en' : 'es') as Locale
  const display = localizedItem(item, locale)
  const cardPrice = effectiveItemCardPrice(item.price, pricingCfg)
  // Chips rápidos del local (globales + de la categoría del ítem) — mismos del POS/kiosk.

  const groups: ModifierGroup[] = useMemo(
    () =>
      renderableGroups(
        (item.item_modifier_groups ?? [])
          .map((g) => g.modifier_groups)
          .filter((g): g is ModifierGroup => !!g)
      ) as ModifierGroup[],
    [item.item_modifier_groups]
  )

  // ── Pizza por zonas ────────────────────────────────────────────────────────
  const isZoned = item.zoned_build === true

  /** Paleta estable por ingrediente: el mismo nombre siempre sale del mismo color,
   *  así la pizza no cambia de aspecto entre visitas. */
  const zonedGroups: ZonedGroup[] = useMemo(() => {
    if (!isZoned) return []
    const receta = new Set((item.item_default_options ?? []).map((r) => r.modifier_option_id))
    const PALETA = ['#B23A2E','#3E4A34','#B9A48A','#4E7A32','#D9C7D2','#E8C24A','#8E4436','#D8B98C','#F2D479','#C0392B','#4C8B3F','#A8763C']
    return groups.map((g) => ({
      id: g.id,
      name: modifierGroupLabel(g),
      zonable: g.zonable === true,
      // Selección única: es lo que impide marcar "Sin queso", "Normal" y "Doble"
      // a la vez. Sale de max_selections, no de una lista hardcodeada de nombres.
      single: (g.max_selections ?? 1) === 1,
      // Las reglas min/max viajan al armador tal como el servidor las va a exigir
      // en el checkout (validateZonedLoad): sin ellas, el armador dejaba pasar el
      // 5º ingrediente gratis de un grupo "elige 4" y el 422 aparecía al pagar.
      required: g.required === true,
      minSelections: g.min_selections ?? 0,
      maxSelections: g.max_selections ?? 1,
      isSize: g.id === item.zone_size_group_id,
      options: g.modifier_options
        // Los agotados YA NO se filtran: entran apagados, igual que en el modal
        // normal. Lo que impedía mostrarlos era que un ingrediente `isDefault`
        // se AUTO-COLOCA en la pizza, así que uno agotado se pre-cargaba en el
        // pedido sin que nadie lo tocara. Eso lo resuelve ahora
        // `seedDefaultOptionIds` (lib/menu/zonedDefaults), que los deja fuera
        // de la siembra sin sacarlos de la vista.
        .map((o) => {
          let h = 0
          for (let i = 0; i < o.name.length; i++) h = (h * 31 + o.name.charCodeAt(i)) >>> 0
          return {
            id: o.id,
            name: o.name,
            color: PALETA[h % PALETA.length],
            basePrice: o.price_delta,
            // 🔴 Dos fuentes de "incluido", y las DOS cuentan:
            //   · is_default → el grupo lo trae por defecto (mozzarella en todas)
            //   · la receta  → ESTE ítem lo trae (jamón y piña SOLO en la Hawaiana)
            // El servidor ya lee las dos (loadZonedQuote.optionFactsFor). Si acá
            // leyéramos una sola, la pantalla cobraría un extra que la caja regala.
            isDefault: o.is_default === true || receta.has(o.id),
            soldOut: o.is_available === false,
          }
        }),
    }))
      // Un grupo cuyas opciones están TODAS agotadas se sigue ocultando, igual
      // que en `renderableGroups`: si es obligatorio pediría un mínimo
      // imposible y dejaría "Agregar" trabado para siempre. Se ocultan grupos
      // enteros, nunca ingredientes sueltos.
      .filter((g) => g.options.some((o) => !o.soldOut))
  }, [isZoned, groups, item.zone_size_group_id, item.item_default_options])

  function handleAddZoned(res: {
    layout: 'whole' | 'halves' | 'quarters'
    selections: PricedZonedSelection[]
    modifiersTotal: number
    sizeOptionId: string | null
  }) {
    const byId = new Map(zonedGroups.flatMap((g) => g.options.map((o) => [o.id, { ...o, groupId: g.id }] as const)))
    // El precio lo RECOTIZA el servidor contra la rejilla —`ZonedSelectionInput` ni
    // siquiera tiene `price_delta`—, así que esto no cobra. Va cotizado porque el
    // comentario de antes tenía razón y el código hacía lo contrario: con 0, el
    // carrito mentía antes de cobrar.
    const mods: CartItem['modifiers'] = zonedCartRows(res.selections, (id) => {
      const o = byId.get(id)
      return o ? { groupId: o.groupId, name: o.name } : undefined
    })
    // El tamaño viaja como un modificador más: es una opción de su grupo y el
    // servidor lo necesita para resolver la rejilla.
    if (res.sizeOptionId) {
      const g = groups.find((x) => x.id === item.zone_size_group_id)
      const so = g?.modifier_options.find((o) => o.id === res.sizeOptionId)
      if (g && so) {
        mods.unshift({
          modifier_group_id: g.id,
          modifier_option_id: so.id,
          option_name: so.name,
          price_delta: effectiveUnitPrice(so.price_delta, pricingCfg),
          quantity: 1,
          zone: 'whole',
          is_removal: false,
        })
      }
    }
    onAdd({
      // eslint-disable-next-line react-hooks/purity
      cartId: `${item.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      itemId: item.id,
      name: item.name,
      price: cardPrice,
      quantity,
      modifiers: mods,
      specialInstructions: specialInstructions.trim() || undefined,
      zoneLayout: res.layout,
      zoneSizeOptionId: res.sizeOptionId,
    })
  }

  const [quantity, setQuantity] = useState(1)
  const [selections, setSelections] = useState<Record<string, SelectedOption[]>>({})

  // ── Súper online (Tarea 2b.2): peso o variante ─────────────────────────────
  // Solo para lo que llegó con `online_retail_v2` (la fila trae weight_unit /
  // variants). Un plato nunca entra acá: `isRetail` es false y el modal es el de main.
  const isRetail = needsRetailPicker(item)
  const retailUnit = isRetailWeightItem(item) ? onlineWeightUnitOf(item.weight_unit) : null
  const [weightMilli, setWeightMilli] = useState(() => (retailUnit ? initialWeightMilli(retailUnit) : 0))
  // Variante: arranca en la primera elegible (orden del dueño).
  const [variantId, setVariantId] = useState<string | null>(
    () => selectableVariants(item.variants).find((v) => !v.disabled)?.id ?? null,
  )
  // Stock EN VIVO (Tarea 2b.3): solo para lo retail; un plato llama al hook apagado
  // y sin ids, así que no hay consulta.
  const retailStock = useRetailStock(locationId, isRetail ? [item.id] : [], isRetail)
  const { soldOutVariantIds, stockLeftByVariant } = retailVariantStock(retailStock, item.id)
  // Si la variante elegida se agotó (o el stock llegó después de abrir), la selección
  // pasa a la primera que queda: nunca se arma una línea de algo agotado.
  const sellableVariants = isRetailVariantItem(item)
    ? selectableVariants(item.variants, soldOutVariantIds).filter((v) => !v.disabled)
    : []
  const chosenVariantId = isRetailVariantItem(item)
    ? (sellableVariants.find((v) => v.id === variantId) ?? sellableVariants[0])?.id ?? null
    : variantId
  const weightBadge = retailUnit ? retailStockBadge(retailStock, { itemId: item.id, weightMilli }) : null
  // Agotado: por peso sin stock, o con variantes y ninguna vendible (incluye `variants: []`).
  const retailSoldOut = isRetail && (retailUnit ? weightBadge?.kind === 'sold_out' : isRetailVariantItem(item) && sellableVariants.length === 0)
  // La MISMA línea que se agregaría: de ella sale el total del botón (una sola cuenta).
  const retailLine = isRetail
    ? buildRetailCartLine({ item, cartId: '', weightMilli, variantId: chosenVariantId, soldOutVariantIds })
    : null
  const [specialInstructions, setSpecialInstructions] = useState('')
  const [error, setError] = useState<string | null>(null)

  // ── Reglas del grupo / opciones (capa pura compartida) ──────────────────────
  const ruleOf = (group: ModifierGroup): ModGroupRule => normalizeGroupRule(group)
  const optionRulesOf = (group: ModifierGroup): ModOptionRule[] =>
    group.modifier_options.map((o) => ({
      priceDelta: effectiveUnitPrice(o.price_delta, pricingCfg),
    }))

  function isSingleSelect(group: ModifierGroup) {
    const max = group.max_selections ?? (group.required ? 1 : null)
    return max === 1
  }

  function groupUnits(groupId: string): number {
    return (selections[groupId] ?? []).reduce((s, x) => s + x.quantity, 0)
  }

  function qtyOf(groupId: string, optionId: string): number {
    return (selections[groupId] ?? []).find((s) => s.modifier_option_id === optionId)?.quantity ?? 0
  }

  function makeEntry(group: ModifierGroup, option: ModifierOption, qty: number): SelectedOption {
    return {
      modifier_group_id: group.id,
      modifier_option_id: option.id,
      option_name: option.name,
      price_delta: effectiveUnitPrice(option.price_delta, pricingCfg),
      quantity: qty,
    }
  }

  // Toggle para opciones NO repetibles (single-select o multi de remoción/gratis).
  function toggleOption(group: ModifierGroup, option: ModifierOption) {
    // 86: la opción se dibuja apagada y sin onClick, pero el guard va igual.
    // /session también rechaza un ingrediente agotado antes de cobrar
    // (lib/online-orders/pricing.ts, 409 modifiers_unavailable); este guard
    // evita que el cliente se entere recién en el pago.
    if (option.is_available === false) return
    setError(null)
    setSelections((prev) => {
      const current = prev[group.id] ?? []
      const exists = current.find((s) => s.modifier_option_id === option.id)

      if (isSingleSelect(group)) {
        return { ...prev, [group.id]: exists ? [] : [makeEntry(group, option, 1)] }
      }
      if (exists) {
        return {
          ...prev,
          [group.id]: current.filter((s) => s.modifier_option_id !== option.id),
        }
      }
      // Tope por suma de unidades del grupo (no repetible → cada opción cuenta 1).
      const cap = groupMaxUnits(ruleOf(group), optionRulesOf(group))
      if (current.reduce((s, x) => s + x.quantity, 0) >= cap) return prev
      return { ...prev, [group.id]: [...current, makeEntry(group, option, 1)] }
    })
  }

  // Stepper para opciones repetibles (extras pagos o grupos de conteo requerido).
  function changeQty(group: ModifierGroup, option: ModifierOption, delta: number) {
    if (option.is_available === false) return // ver toggleOption
    setError(null)
    setSelections((prev) => {
      const current = prev[group.id] ?? []
      const cap = groupMaxUnits(ruleOf(group), optionRulesOf(group))
      const existing = current.find((s) => s.modifier_option_id === option.id)
      const curQty = existing?.quantity ?? 0
      const othersTotal = current.reduce((s, x) => s + x.quantity, 0) - curQty
      let nextQty = Math.min(Math.max(0, curQty + delta), MAX_MODIFIER_QTY)
      // No exceder el tope del grupo (suma de cantidades).
      if (othersTotal + nextQty > cap) nextQty = Math.max(0, cap - othersTotal)

      if (nextQty === 0) {
        return { ...prev, [group.id]: current.filter((s) => s.modifier_option_id !== option.id) }
      }
      if (existing) {
        return {
          ...prev,
          [group.id]: current.map((s) =>
            s.modifier_option_id === option.id ? { ...s, quantity: nextQty } : s
          ),
        }
      }
      return { ...prev, [group.id]: [...current, makeEntry(group, option, nextQty)] }
    })
  }

  const allSelected = useMemo(() => Object.values(selections).flat(), [selections])

  // Selecciones incluidas (el mixto): la línea del carrito con precio y rol por fila,
  // en el orden de toque (cada grupo guarda su arreglo en ese orden). Es la MISMA línea
  // que se agrega al carrito y de la que sale el total de este modal: una sola cuenta,
  // igual a la del servidor. El menú web guarda el precio EFECTIVO. La pizza por zonas
  // la cotiza su propio motor, que no aplica esta regla.
  const lineModifiers = useMemo(
    () =>
      item.zoned_build === true
        ? allSelected
        : applyRulesToLineModifiers(allSelected, groups, (raw) => effectiveUnitPrice(raw, pricingCfg)),
    [allSelected, groups, item.zoned_build, pricingCfg]
  )

  const modifiersSubtotal = useMemo(
    () =>
      modifierUnitsSubtotal(
        lineModifiers.map((s) => ({ priceDelta: s.price_delta, quantity: s.quantity }))
      ),
    [lineModifiers]
  )

  /** Selecciones incluidas: el rótulo que reemplaza al precio de cada opción. */
  function ruleNoteText(group: ModifierGroup, optionId: string, isSelected: boolean): string | null {
    const ordered = (selections[group.id] ?? []).map((sel) => ({ optionId: sel.modifier_option_id, quantity: sel.quantity }))
    const note = ruleNoteOf(group, ordered, optionId, isSelected, (raw) => effectiveUnitPrice(raw, pricingCfg))
    if (!note) return null
    if (note.kind === 'add') return note.amount > 0 ? `+$${note.amount.toFixed(2)}` : tRules('roleIncluded')
    if (note.role === 'extra') return `${tRules('roleExtra')} +$${note.amount.toFixed(2)}`
    return note.role === 'shared' ? tRules('roleShared') : tRules('roleIncluded')
  }
  const totalUnitPrice = cardPrice + modifiersSubtotal
  const totalPrice = isRetail
    ? Math.round((retailLine ? micrositeLineSubtotal(retailLine, pricingCfg) : 0) * 100) / 100
    : Math.round(totalUnitPrice * quantity * 100) / 100

  function validate(): string | null {
    for (const g of groups) {
      const totalUnits = groupUnits(g.id)
      if (!groupSelectionValid(ruleOf(g), optionRulesOf(g), totalUnits)) {
        const min = g.min_selections ?? (g.required ? 1 : 0)
        const label = modifierGroupLabel(g)
        if (totalUnits < min) {
          return `Selecciona al menos ${min} opción${min > 1 ? 'es' : ''} en "${label}"`
        }
        return `Revisa tu selección en "${label}"`
      }
    }
    return null
  }

  function handleAdd() {
    if (isRetail) {
      if (retailSoldOut) return
      // Cantidad 1 + peso o variante, precio CRUDO (R5), sin modificadores.
      const line = buildRetailCartLine({
        item,
        // eslint-disable-next-line react-hooks/purity
        cartId: `${item.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        weightMilli,
        variantId: chosenVariantId,
        note: specialInstructions,
        soldOutVariantIds,
      })
      if (line) onAdd(line)
      return
    }
    const err = validate()
    if (err) {
      setError(err)
      return
    }
    const cartItem: CartItem = {
      // eslint-disable-next-line react-hooks/purity
      cartId: `${item.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      itemId: item.id,
      name: item.name,
      // Card price (after surcharge), per unit — modifiers tracked separately
      price: cardPrice,
      quantity,
      modifiers: lineModifiers,
      specialInstructions: specialInstructions.trim() || undefined,
    }
    onAdd(cartItem)
  }

  return (
    <SheetPortal>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="relative w-full max-w-2xl bg-white rounded-t-3xl sm:rounded-3xl flex flex-col"
        style={{ maxHeight: '90dvh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Scrollable content area */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {/* Close */}
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5 text-gray-700" />
          </button>

          {/* Image */}
          {item.image_url && (
            <div className="relative w-full aspect-[16/9] bg-gray-100">
              <Image
                src={menuPhotoUrl(item.image_url, 900)}
                alt={display.name}
                fill
                sizes="(max-width: 768px) 100vw, 768px"
                className="object-cover"
                style={{ objectPosition: focalPosition(item.image_focal) }}
              />
            </div>
          )}

          <div className="p-5">
            <h2 className="text-xl font-bold text-gray-900">{display.name}</h2>
            {display.description && (
              <p className="text-sm text-gray-600 mt-1">{display.description}</p>
            )}
            <p
              className="text-lg font-bold mt-2"
              style={{ color: accentColor }}
            >
              {retailUnit
                ? `$${effectiveUnitPrice(item.price, pricingCfg).toFixed(2)}/${retailUnit}`
                : isRetail && retailLine
                  ? `$${micrositeLineSubtotal(retailLine, pricingCfg).toFixed(2)}`
                  : `$${cardPrice.toFixed(2)}`}
            </p>

            {isRetail ? (
              <RetailLinePicker
                item={item}
                pricingCfg={pricingCfg}
                accentColor={accentColor}
                locale={locale}
                weightMilli={weightMilli}
                onWeightChange={setWeightMilli}
                variantId={chosenVariantId}
                onVariantChange={setVariantId}
                soldOutVariantIds={soldOutVariantIds}
                stockLeftByVariant={stockLeftByVariant}
                weightStockLeft={weightBadge?.kind === 'low' ? weightBadge.left : undefined}
              />
            ) : null}

            {/* Quantity. En pizza NO se muestra acá: el armador ya la pide en su
                sitio (Tamaño → Cantidad → …). Comparten el mismo estado, así que
                no había descuadre, pero salían dos steppers iguales en pantalla. */}
            {isRetail ? null : (
            <div className={isZoned ? "hidden" : "flex items-center justify-between mt-5"}>
              <span className="text-sm font-semibold text-gray-700">Cantidad</span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className="w-9 h-9 rounded-full bg-gray-100 text-gray-700 font-bold hover:bg-gray-200 disabled:opacity-50"
                  disabled={quantity <= 1}
                >
                  −
                </button>
                <span className="w-6 text-center font-semibold">{quantity}</span>
                <button
                  type="button"
                  onClick={() => setQuantity((q) => q + 1)}
                  className="w-9 h-9 rounded-full bg-gray-100 text-gray-700 font-bold hover:bg-gray-200"
                >
                  +
                </button>
              </div>
            </div>
            )}

            {/* Pizza por zonas: reemplaza la lista de grupos por el armador. Solo
                para ítems con `zoned_build`; el resto del menú no se entera. */}
            {isZoned ? (
              <ZonedBuilder
                itemName={display.name}
                basePrice={cardPrice}
                groups={zonedGroups}
                mode={zonedFacts.mode}
                sizeGrid={zonedFacts.grid}
                cfg={pricingCfg}
                quantity={quantity}
                onQuantityChange={setQuantity}
                onAdd={handleAddZoned}
              />
            ) : null}

            {/* Modifier groups (lo retail no lleva: el servidor los rechaza) */}
            {isZoned || isRetail ? null : groups.map((group) => {
              const single = isSingleSelect(group)
              const rule = ruleOf(group)
              const optionRules = optionRulesOf(group)
              const cap = groupMaxUnits(rule, optionRules)
              const usedUnits = groupUnits(group.id)
              // Recibe la opción entera, no el nombre: el helper aplica la
              // regla de disponibilidad. Usa `option.name` canónico, NUNCA
              // localizedName(...). `group.name` (también canónico) es el
              // contexto que desambigua nombres como 'pollo' — el MISMO
              // `photoOf` de acá abajo decide encendido (groupShowsPhotos),
              // orden (orderOptionsByPhoto) y pintado (las <img> más abajo):
              // si alguno usara un contexto distinto, un grupo podría
              // encenderse por una opción que después no pinta su foto.
              const photoOf = (option: ModifierOption) => resolveGroupOptionPhoto(option, group)
              const showPhotos = groupShowsPhotos(group.modifier_options, photoOf)
              return (
                <div key={group.id} className="mt-5">
                  <div className="flex items-baseline justify-between">
                    <h3 className="font-semibold text-gray-900">{localizedGroupLabel(group, locale)}</h3>
                    {group.required && (
                      <span className="text-xs font-semibold uppercase text-red-600">
                        Requerido
                      </span>
                    )}
                  </div>
                  {(() => {
                    const r = groupRule(group)
                    if (!r) return null
                    const price = `$${effectiveUnitPrice(r.extraSelectionPrice, pricingCfg).toFixed(2)}`
                    return (
                      <p className="mt-0.5 text-xs text-gray-500">
                        {r.sharedPortion
                          ? tRules('includedRuleShared', { count: r.includedSelections, price })
                          : tRules('includedRule', { count: r.includedSelections, price })}
                      </p>
                    )
                  })()}
                  <div className="mt-2 space-y-2">
                    {orderOptionsByPhoto(group.modifier_options, photoOf)
                      .map((option) => withLowStockTag(option.id, option.is_available !== false && lowStock.option(option.id), (() => {
                        const optDelta = effectiveUnitPrice(option.price_delta, pricingCfg)
                        const repeatable = canRepeat(rule, { priceDelta: optDelta })
                        const qty = qtyOf(group.id, option.id)

                        // ── Agotado (86): visible pero no elegible ──────────
                        // Antes se filtraba y el ingrediente DESAPARECÍA de la
                        // lista: el cliente no entendía si se acabó hoy o si el
                        // local dejó de ofrecerlo. `optionRulesOf` ya mapeaba
                        // todas las opciones, así que los topes del grupo no
                        // cambian por mostrarla.
                        if (option.is_available === false) {
                          return (
                            <div
                              key={option.id}
                              aria-disabled="true"
                              className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50"
                            >
                              <span className="text-sm text-gray-400 line-through">
                                {localizedName(option, locale)}
                              </span>
                              <span className="text-[11px] font-bold uppercase tracking-wide text-gray-500">
                                {t('soldOut')}
                              </span>
                            </div>
                          )
                        }

                        // ── Stepper (repetible: extra pago o conteo requerido) ──
                        if (repeatable) {
                          const canInc = usedUnits < cap && qty < MAX_MODIFIER_QTY
                          return (
                            <div
                              key={option.id}
                              className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border transition ${
                                qty > 0 ? 'border-gray-900 bg-gray-50' : 'border-gray-200'
                              }`}
                            >
                              {showPhotos &&
                                (photoOf(option) ? (
                                  <img
                                    src={menuPhotoUrl(photoOf(option), 160)!}
                                    alt=""
                                    loading="lazy"
                                    className="w-16 h-16 rounded-[10px] object-cover flex-none"
                                  />
                                ) : (
                                  <span className="w-16 h-16 rounded-[10px] bg-gray-100 flex-none" aria-hidden="true" />
                                ))}
                              <span className="flex items-center gap-2">
                                <span className="text-sm text-gray-900">{localizedName(option, locale)}</span>
                                {(() => {
                                  const note = ruleNoteText(group, option.id, qty > 0)
                                  if (note !== null) return <span className="text-sm text-gray-600">{note}</span>
                                  return optDelta !== 0 ? (
                                    <span className="text-sm text-gray-600">
                                      {optDelta > 0 ? '+' : ''}${Math.abs(optDelta).toFixed(2)}
                                    </span>
                                  ) : null
                                })()}
                              </span>
                              {qty === 0 ? (
                                <button
                                  type="button"
                                  onClick={() => changeQty(group, option, +1)}
                                  disabled={!canInc}
                                  className="px-4 h-8 rounded-full text-sm font-bold text-white disabled:opacity-40"
                                  style={{ backgroundColor: accentColor }}
                                >
                                  Agregar
                                </button>
                              ) : (
                                <span className="flex items-center gap-2.5">
                                  <button
                                    type="button"
                                    onClick={() => changeQty(group, option, -1)}
                                    className="w-8 h-8 rounded-full bg-gray-100 text-gray-700 font-bold hover:bg-gray-200"
                                    aria-label={`Quitar uno de ${localizedName(option, locale)}`}
                                  >
                                    −
                                  </button>
                                  <span className="w-5 text-center font-semibold text-sm">{qty}</span>
                                  <button
                                    type="button"
                                    onClick={() => changeQty(group, option, +1)}
                                    disabled={!canInc}
                                    className="w-8 h-8 rounded-full bg-gray-100 text-gray-700 font-bold hover:bg-gray-200 disabled:opacity-40"
                                    aria-label={`Agregar uno de ${localizedName(option, locale)}`}
                                  >
                                    +
                                  </button>
                                </span>
                              )}
                            </div>
                          )
                        }

                        // ── Toggle (no repetible: checkbox/radio actual) ──
                        const selected = qty > 0
                        return (
                          <button
                            key={option.id}
                            type="button"
                            onClick={() => toggleOption(group, option)}
                            className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border transition ${
                              selected
                                ? 'border-gray-900 bg-gray-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <span className="flex items-center gap-3">
                              <span
                                className={`w-5 h-5 flex items-center justify-center ${
                                  single ? 'rounded-full' : 'rounded'
                                } border-2 ${
                                  selected ? 'border-gray-900' : 'border-gray-300'
                                }`}
                                style={
                                  selected
                                    ? { backgroundColor: accentColor, borderColor: accentColor }
                                    : undefined
                                }
                              >
                                {selected && (
                                  <span
                                    className={`block ${
                                      single ? 'w-2 h-2 rounded-full bg-white' : 'text-white text-xs'
                                    }`}
                                  >
                                    {single ? null : '✓'}
                                  </span>
                                )}
                              </span>
                              {showPhotos &&
                                (photoOf(option) ? (
                                  <img
                                    src={menuPhotoUrl(photoOf(option), 160)!}
                                    alt=""
                                    loading="lazy"
                                    className="w-16 h-16 rounded-[10px] object-cover flex-none"
                                  />
                                ) : (
                                  <span className="w-16 h-16 rounded-[10px] bg-gray-100 flex-none" aria-hidden="true" />
                                ))}
                              <span className="text-sm text-gray-900">{localizedName(option, locale)}</span>
                              {option.uses_all_included === true && groupRule(group) && (
                                <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
                                  {tRules('goesAlone')}
                                </span>
                              )}
                            </span>
                            {(() => {
                              const note = ruleNoteText(group, option.id, selected)
                              if (note !== null) return <span className="text-sm text-gray-600">{note}</span>
                              return optDelta !== 0 ? (
                                <span className="text-sm text-gray-600">
                                  {optDelta > 0 ? '+' : ''}$
                                  {Math.abs(optDelta).toFixed(2)}
                                </span>
                              ) : null
                            })()}
                          </button>
                        )
                      })()))}
                  </div>
                </div>
              )
            })}

            {/* Special instructions: texto libre. Viaja como special_instructions y
                llega al KDS/ticket de cocina. Los chips de `quick_notes` NO van acá:
                son una herramienta interna del POS (ver lib/menu/guidedSteps.ts). */}
            <div className="mt-5">
              <label className="text-sm font-semibold text-gray-700">
                {t('itemNotesLabel')}
              </label>
              <textarea
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value.slice(0, MAX_NOTE_LEN))}
                placeholder={t('itemNotesPlaceholder')}
                className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-gray-900/10"
                rows={2}
              />
              <div className="flex justify-end">
                <span className="text-[11px] text-gray-400">
                  {specialInstructions.length}/{MAX_NOTE_LEN}
                </span>
              </div>
            </div>

            {error && (
              <p className="mt-3 text-sm text-red-600">{error}</p>
            )}
          </div>
        </div>

        {/* Sticky bottom — el armador por zonas trae su propio total y su botón,
            así que acá se oculta para no ofrecer dos "Agregar" que hacen cosas
            distintas. */}
        {isZoned ? null : <div
          className="flex-shrink-0 px-4 pt-4 border-t border-gray-100 bg-white"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            onClick={handleAdd}
            disabled={isRetail && (!retailLine || retailSoldOut)}
            className={`w-full rounded-full py-4 text-white text-base font-bold transition hover:opacity-95 active:scale-[0.99]${isRetail ? ' disabled:opacity-50' : ''}`}
            style={{ backgroundColor: accentColor }}
          >
            {retailSoldOut ? t('soldOut') : <>Agregar al carrito · ${totalPrice.toFixed(2)}</>}
          </button>
        </div>}
      </motion.div>
    </motion.div>
    </SheetPortal>
  )
}
